import React, { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { penaltyPresetsAPI, QUERY_KEYS, useBranches, usePenaltyPresets } from '@/api';
import { Badge, Button, Card, CardContent, Input, Modal } from '@/components/ui';
import { BranchFilterSelect, LoadingSpinner, Pagination } from '@/components/common';
import { Edit2, Plus, Tag, Trash2 } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { paginateClientData } from '@/utils/pagination';

const formatAmount = (value: any) => Number(value || 0).toLocaleString('ru-RU');
const FILTER_SELECT_CLASS = 'min-h-[2.5rem] w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2 text-[13px] text-slate-700 shadow-sm outline-none transition focus:border-blue-400 focus:ring-2 focus:ring-blue-500/15';

function PresetMetricCard({
  icon,
  label,
  value,
  helper,
  iconClassName,
}: {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  helper?: string;
  iconClassName: string;
}) {
  return (
    <Card className="border-slate-200/90 bg-white/95">
      <CardContent className="flex items-center gap-3 p-4">
        <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${iconClassName}`}>
          {icon}
        </div>
        <div className="min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">{label}</p>
          <p className="mt-1 text-xl font-bold tracking-tight text-slate-900 sm:text-[1.4rem]">{value}</p>
          {helper && <p className="mt-1 text-[11px] leading-4 text-slate-500">{helper}</p>}
        </div>
      </CardContent>
    </Card>
  );
}

export default function PenaltyPresetsPage() {
  const perPage = 10;
  const { can } = useAuth();
  const canManage = can('manage_penalties');
  const queryClient = useQueryClient();

  const [page, setPage] = useState(1);
  const [showForm, setShowForm] = useState(false);
  const [editingPreset, setEditingPreset] = useState<any>(null);
  const [search, setSearch] = useState('');
  const [branchFilter, setBranchFilter] = useState('');
  const [activeFilter, setActiveFilter] = useState('');

  const isActive =
    activeFilter === ''
      ? ''
      : activeFilter === 'active';

  const { data: presetsData, isLoading } = usePenaltyPresets({
    branch_id: branchFilter,
    is_active: isActive,
  });
  const { data: branchesData } = useBranches({ page: 1, search: '' });

  const createMutation = useMutation({
    mutationFn: (data: any) => penaltyPresetsAPI.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.penaltyPresets() });
      setShowForm(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => penaltyPresetsAPI.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.penaltyPresets() });
      setEditingPreset(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => penaltyPresetsAPI.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.penaltyPresets() });
    },
  });

  const presets = Array.isArray(presetsData?.data)
    ? presetsData.data
    : presetsData?.data?.data || [];
  const branches = Array.isArray(branchesData?.data)
    ? branchesData.data
    : branchesData?.data?.data || [];

  const filteredPresets = useMemo(() => {
    if (!search.trim()) {
      return presets;
    }

    const query = search.toLowerCase();

    return presets.filter((preset: any) =>
      [preset.name, preset.code, preset.description]
        .filter(Boolean)
        .some((value: string) => value.toLowerCase().includes(query))
    );
  }, [presets, search]);
  const { items: pagedPresets, meta } = paginateClientData<any>(filteredPresets, page, perPage);

  const handleDelete = async (id: number) => {
    if (!window.confirm('Удалить этот тип штрафа?')) {
      return;
    }

    await deleteMutation.mutateAsync(id);
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  const activePresetsCount = presets.filter((preset: any) => preset.is_active).length;
  const averageAmount = presets.length
    ? formatAmount(
        presets.reduce((sum: number, preset: any) => sum + Number(preset.amount || 0), 0) / presets.length
      )
    : '0';

  return (
    <div className="penalty-compact-page space-y-5 page-shell">
      <Card className="overflow-hidden border-slate-200/90 bg-[radial-gradient(circle_at_top_left,_rgba(59,130,246,0.18),_transparent_38%),linear-gradient(135deg,_rgba(255,255,255,0.98),_rgba(239,246,255,0.96))]">
        <CardContent className="p-4 sm:p-5 lg:p-6">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
            <div className="max-w-3xl">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-blue-600/70">Типы штрафов</p>
              <h1 className="mt-2 text-[1.9rem] font-bold tracking-tight text-slate-950 sm:text-[2.25rem]">Шаблоны ручных штрафов</h1>
              <p className="mt-2 max-w-2xl text-[13px] leading-5 text-slate-600 sm:text-sm">
                Здесь собраны типы штрафов для ручного начисления. Код создается автоматически, поэтому вы заполняете только название, описание, филиал и сумму.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <span className="rounded-full border border-white/70 bg-white/80 px-3 py-1 text-xs text-slate-700 shadow-sm">
                  Всего типов: <strong className="text-slate-900">{presets.length}</strong>
                </span>
                <span className="rounded-full border border-white/70 bg-white/80 px-3 py-1 text-xs text-slate-700 shadow-sm">
                  Активные: <strong className="text-slate-900">{activePresetsCount}</strong>
                </span>
                <span className="rounded-full border border-white/70 bg-white/80 px-3 py-1 text-xs text-slate-700 shadow-sm">
                  Средняя сумма: <strong className="text-slate-900">{averageAmount}</strong>
                </span>
              </div>
            </div>

            {canManage && (
              <div className="flex w-full flex-col gap-3 sm:flex-row xl:w-auto">
                <Button size="sm" className="shadow-sm shadow-blue-500/20" onClick={() => setShowForm(true)}>
                  <Plus className="mr-2 h-4 w-4" />
                  Добавить тип
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Modal
        isOpen={showForm || !!editingPreset}
        onClose={() => {
          setShowForm(false);
          setEditingPreset(null);
        }}
        title={editingPreset ? 'Редактировать тип штрафа' : 'Новый тип штрафа'}
      >
        <PenaltyPresetForm
          initialData={editingPreset}
          branches={branches}
          onSubmit={async (data) => {
            if (editingPreset) {
              await updateMutation.mutateAsync({ id: editingPreset.id, data });
            } else {
              await createMutation.mutateAsync(data);
            }
          }}
          onCancel={() => {
            setShowForm(false);
            setEditingPreset(null);
          }}
        />
      </Modal>

      <Card>
        <CardContent className="space-y-3 py-3.5">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">Фильтры</p>
            <h2 className="mt-1 text-base font-semibold text-slate-900">Поиск и быстрый отбор</h2>
          </div>
          <div className="grid gap-4 md:grid-cols-3">
            <Input
              className="min-h-[2.5rem] border-slate-200 bg-white px-3.5 text-[13px] shadow-sm focus:border-blue-400 focus:ring-blue-500/15"
              placeholder="Поиск по названию, коду или описанию"
              value={search}
              onChange={(e) => {
                setPage(1);
                setSearch(e.target.value);
              }}
            />
            <BranchFilterSelect
              alwaysShow
              className={FILTER_SELECT_CLASS}
              value={branchFilter}
              onChange={(value) => {
                setPage(1);
                setBranchFilter(value);
              }}
            />
            <select
              className={FILTER_SELECT_CLASS}
              value={activeFilter}
              onChange={(e) => {
                setPage(1);
                setActiveFilter(e.target.value);
              }}
            >
              <option value="">Все статусы</option>
              <option value="active">Только активные</option>
              <option value="inactive">Только неактивные</option>
            </select>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-4 md:grid-cols-3">
        <PresetMetricCard
          icon={<Tag className="h-5 w-5 text-blue-600" />}
          iconClassName="bg-blue-100"
          label="Всего"
          value={presets.length}
          helper="Количество записей по текущему фильтру"
        />
        <PresetMetricCard
          icon={<Plus className="h-5 w-5 text-emerald-600" />}
          iconClassName="bg-emerald-100"
          label="Активные"
          value={activePresetsCount}
          helper="Доступны для ручного начисления"
        />
        <PresetMetricCard
          icon={<Tag className="h-5 w-5 text-amber-600" />}
          iconClassName="bg-amber-100"
          label="Средняя сумма"
          value={averageAmount}
          helper="Средний размер штрафа по списку"
        />
      </div>

      <Card className="border-slate-200/90 bg-white/95">
        <div className="flex flex-col gap-3 border-b border-slate-100 px-3.5 py-3 sm:px-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">Список</p>
            <h2 className="mt-1 text-lg font-semibold tracking-tight text-slate-900">Таблица типов штрафов</h2>
            <p className="mt-1 text-[13px] text-slate-500">Компактный вид: название, краткое описание, филиал, сумма и статус.</p>
          </div>
          <span className="rounded-full bg-slate-100 px-3 py-1 text-xs text-slate-700">
            Фильтр: <strong className="text-slate-900">{branchFilter ? branches.find((branch: any) => String(branch.id) === branchFilter)?.name || 'Филиал' : 'Все филиалы'}</strong>
          </span>
        </div>

        <div className="overflow-x-auto ui-table-wrap">
          <table className="w-full ui-table">
            <colgroup>
              <col className="w-[280px]" />
              <col className="w-[360px]" />
              <col className="w-[190px]" />
              <col className="w-[130px]" />
              <col className="w-[130px]" />
              <col className="w-[120px]" />
            </colgroup>
            <thead className="border-b bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left font-semibold">Название</th>
                <th className="px-4 py-3 text-left font-semibold">Описание</th>
                <th className="px-4 py-3 text-left font-semibold">Филиал</th>
                <th className="px-4 py-3 text-left font-semibold">Сумма</th>
                <th className="px-4 py-3 text-left font-semibold">Статус</th>
                <th className="px-4 py-3 text-left font-semibold">Действия</th>
              </tr>
            </thead>
            <tbody>
              {pagedPresets.map((preset: any) => (
                <tr key={preset.id} className="border-b hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="min-w-0">
                      <div className="truncate font-medium text-slate-900" title={preset.name}>{preset.name}</div>
                      <div className="mt-1 truncate text-[11px] uppercase tracking-[0.14em] text-slate-400" title={preset.code}>
                        {preset.code}
                      </div>
                    </div>
                  </td>
                  <td className="compact-subtext px-4 py-3 text-gray-500">
                    <p className="compact-description" title={preset.description || 'Описание отсутствует'}>
                      {preset.description || 'Описание отсутствует'}
                    </p>
                  </td>
                  <td className="px-4 py-3 text-[13px]">
                    <div className="max-w-[180px] truncate" title={preset.branch?.name || 'Все филиалы'}>{preset.branch?.name || 'Все филиалы'}</div>
                  </td>
                  <td className="ui-nowrap px-4 py-3 text-[13px] font-medium">{formatAmount(preset.amount)}</td>
                  <td className="compact-badge ui-nowrap px-4 py-3">
                    <Badge variant={preset.is_active ? 'success' : 'secondary'}>
                      {preset.is_active ? 'Активный' : 'Неактивный'}
                    </Badge>
                  </td>
                  <td className="ui-nowrap px-4 py-3">
                    {canManage && (
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" className="h-9 w-9 px-0" onClick={() => setEditingPreset(preset)}>
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="outline" className="h-9 w-9 px-0" onClick={() => handleDelete(preset.id)}>
                          <Trash2 className="h-4 w-4 text-red-600" />
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
      <Pagination meta={meta} onPageChange={setPage} />

      {pagedPresets.length === 0 && (
        <div className="py-12 text-center">
          <Tag className="mx-auto mb-4 h-16 w-16 text-gray-400" />
          <h3 className="text-lg font-medium text-gray-900">Типы штрафов не найдены</h3>
          <p className="text-gray-500">
            Создайте первый шаблон, чтобы он появился в форме ручного начисления штрафов.
          </p>
        </div>
      )}
    </div>
  );
}

function PenaltyPresetForm({
  initialData,
  branches,
  onSubmit,
  onCancel,
}: {
  initialData?: any;
  branches: any[];
  onSubmit: (data: any) => Promise<void>;
  onCancel: () => void;
}) {
  const [formData, setFormData] = useState({
    branch_id: initialData?.branch_id ? String(initialData.branch_id) : '',
    name: initialData?.name || '',
    description: initialData?.description || '',
    amount: initialData?.amount != null ? String(initialData.amount) : '',
    is_active: initialData?.is_active ?? true,
  });

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      window.alert('Укажите название типа штрафа.');
      return;
    }

    if (formData.amount === '') {
      window.alert('Укажите сумму штрафа.');
      return;
    }

    await onSubmit({
      branch_id: formData.branch_id ? Number(formData.branch_id) : null,
      name: formData.name.trim(),
      description: formData.description.trim() || null,
      amount: Number(formData.amount),
      is_active: formData.is_active,
    });
  };

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-blue-100 bg-blue-50/70 px-4 py-3 text-sm text-slate-600">
        Код создается автоматически после сохранения. В форме нужно указать только смысл штрафа и сумму.
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <label className="mb-1 block text-sm text-gray-500">Сумма</label>
          <Input
            type="number"
            placeholder="Например: 10000"
            value={formData.amount}
            onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
          />
        </div>
      </div>

      <div>
        <label className="mb-1 block text-sm text-gray-500">Название</label>
        <Input
          placeholder="Например: Опоздание"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
        />
      </div>

      <div>
        <label className="mb-1 block text-sm text-gray-500">Описание</label>
        <Input
          placeholder="Краткое описание типа штрафа"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
        />
      </div>

      <div>
        <label className="mb-1 block text-sm text-gray-500">Филиал (необязательно)</label>
        <select
          className={FILTER_SELECT_CLASS}
          value={formData.branch_id}
          onChange={(e) => setFormData({ ...formData, branch_id: e.target.value })}
        >
          <option value="">Все филиалы</option>
          {branches.map((branch: any) => (
            <option key={branch.id} value={branch.id}>
              {branch.name}
            </option>
          ))}
        </select>
      </div>

      <label className="flex items-center gap-2 text-sm text-gray-700">
        <input
          type="checkbox"
          checked={formData.is_active}
          onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
        />
        Активный тип штрафа
      </label>

      <div className="flex gap-2">
        <Button onClick={handleSubmit} className="flex-1">
          Сохранить
        </Button>
        <Button onClick={onCancel} variant="outline" className="flex-1">
          Отмена
        </Button>
      </div>
    </div>
  );
}

